// Componente base genérico para tablas estandarizadas
// Basado en WarehouseTable pero flexible para diferentes tipos de datos
import React from 'react';
import {
  Box,
  TableBody,
  TableCell,
  TableRow,
  Checkbox,
  Tooltip,
  TableSortLabel,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  AddRounded as ExpandAddIcon,
  RemoveRounded as ExpandRemoveIcon,
} from '@mui/icons-material';
import {
  TableContainerStyled,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeadCell,
  DisabledCheckbox,
} from './Table.styles';
import EmptyStateMessage from '../EmptyStateMessage';
import { useTable, TableItem, GetChipPropsFn, getChipPropsDefault } from './Table.hooks';
import { compareValuesNaturally } from '../../../utils/sort/compareValues';
import { getTableRowHoverOverlay } from '../../../styles/theme/tableRowOverlays';

// Definición de columna
interface TableColumn<T extends TableItem> {
  id: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  /** When set with `onRequestSort`, column is server-sorted by this API field name */
  sortField?: string;
  /** Optional accessor for local sort when value is derived or nested. */
  sortAccessor?: (item: T) => unknown;
  render?: (item: T) => React.ReactNode;
  cellStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  /**
   * When false, header is plain text (no sort control). In server-sorted tables
   * (`onRequestSort` set) only columns with `sortField` are sortable: sorting the
   * current page in the browser would look right but only reorder the visible rows.
   */
  sortable?: boolean;
}

// Props del componente base
interface BaseTableProps<T extends TableItem> {
  items: T[];
  columns: TableColumn<T>[];
  selectedItems?: (string | number)[];
  onSelectItems?: (items: (string | number)[]) => void;
  isSelectable?: (item: T) => boolean;
  getItemId?: (item: T) => string | number;
  onViewDetails?: (id: string | number) => void;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
  emptyStateIcon?: string | React.ReactNode;
  showCheckbox?: boolean;
  showActions?: boolean;
  getChipProps?: GetChipPropsFn;
  renderActions?: (item: T) => React.ReactNode;
  className?: string;
  /** Server-side sort: active column id (matches column.sortField) */
  sortBy?: string | null;
  sortOrder?: 'asc' | 'desc';
  onRequestSort?: (sortField: string) => void;
  /** Data cell tone; `muted` matches inventory list tables (Sucursal, Almacén). */
  cellTone?: 'default' | 'muted';
  /** IDs of items whose expanded sub-row (renderExpandedContent) is currently open. */
  expandedItemIds?: (string | number)[];
  /** Renders an inline sub-row (full width, colSpan) below an expanded item. */
  renderExpandedContent?: (item: T) => React.ReactNode;
  /** Called with the item's ID when its expand toggle is clicked. Required to render the toggle column. */
  onToggleExpand?: (itemId: string | number) => void;
  /** Whether a given item can be expanded; items that can't show no toggle. Defaults to always expandable. */
  isRowExpandable?: (item: T) => boolean;
}

/**
 * Componente base genérico para tablas estandarizadas
 */
function BaseTable<T extends TableItem = TableItem>({
  items,
  columns,
  selectedItems = [],
  onSelectItems,
  isSelectable,
  getItemId = (item) => item.id,
  onViewDetails,
  emptyStateTitle = 'No se encontraron elementos',
  showCheckbox = false,
  showActions = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getChipProps = getChipPropsDefault,
  renderActions,
  className,
  sortBy = null,
  sortOrder = 'asc',
  onRequestSort,
  cellTone = 'default',
  expandedItemIds,
  renderExpandedContent,
  onToggleExpand,
  isRowExpandable = () => true,
}: BaseTableProps<T>) {
  /** Seed from props so server-DESC lists show the active arrow on first paint. */
  const [clientSort, setClientSort] = React.useState<{
    sortBy: string | null;
    sortOrder: 'asc' | 'desc';
  }>(() => ({
    sortBy: sortBy ?? null,
    sortOrder: sortOrder ?? 'asc',
  }));

  const { selectableItems, isAllSelected, handleSelectItem, handleSelectAll, getCheckboxStyles } =
    useTable<T>({
      items,
      selectedItems,
      onSelectItems: onSelectItems ?? (() => {}),
      isSelectable,
      getItemId,
    });

  // Calcular número total de columnas
  const hasExpandToggle = renderExpandedContent != null && onToggleExpand != null;
  const totalColumns =
    columns.length + (showCheckbox ? 1 : 0) + (showActions ? 1 : 0) + (hasExpandToggle ? 1 : 0);

  // Verificar si hay selección parcial (indeterminado)
  const isIndeterminate =
    selectedItems.length > 0 &&
    !isAllSelected &&
    selectableItems.some((item: T) => selectedItems.includes(getItemId(item)));

  const hasServerSortSelection =
    onRequestSort != null &&
    sortBy != null &&
    columns.some((column) => column.sortField != null && column.sortField === sortBy);

  const effectiveSortBy = hasServerSortSelection ? sortBy : clientSort.sortBy;
  const effectiveSortOrder = hasServerSortSelection ? sortOrder : clientSort.sortOrder;

  const resolveColumnBySortField = React.useCallback(
    (value: string): TableColumn<T> | undefined =>
      columns.find((column) => (column.sortField ?? column.id) === value),
    [columns],
  );

  const resolveSortValue = React.useCallback((item: T, column: TableColumn<T>): unknown => {
    if (column.sortAccessor != null) {
      return column.sortAccessor(item);
    }
    const field = column.sortField ?? column.id;
    return (item as Record<string, unknown>)[field];
  }, []);

  const displayedItems = React.useMemo(() => {
    if (effectiveSortBy == null) {
      return items;
    }

    const column = resolveColumnBySortField(effectiveSortBy);
    if (column == null) {
      return items;
    }

    const sorted = [...items].sort((a, b) => {
      const aValue = resolveSortValue(a, column);
      const bValue = resolveSortValue(b, column);
      const comparison = compareValuesNaturally(aValue, bValue);
      return effectiveSortOrder === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [items, effectiveSortBy, effectiveSortOrder, resolveColumnBySortField, resolveSortValue]);

  const handleHeaderSort = React.useCallback(
    (field: string) => {
      if (onRequestSort != null) {
        const serverColumn = columns.find((column) => column.sortField === field);
        if (serverColumn != null && serverColumn.sortField != null) {
          onRequestSort(serverColumn.sortField);
          return;
        }
      }
      setClientSort((prev) => {
        if (prev.sortBy === field) {
          return {
            sortBy: field,
            sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
          };
        }
        return { sortBy: field, sortOrder: 'asc' };
      });
    },
    [onRequestSort, columns],
  );

  const isColumnSortable = React.useCallback(
    (column: TableColumn<T>) => {
      if (column.sortable === false) {
        return false;
      }
      if (onRequestSort != null) {
        return column.sortField != null;
      }
      return true;
    },
    [onRequestSort],
  );

  return (
    <TableContainerStyled className={className}>
      <StyledTableContainer>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              {hasExpandToggle && <StyledTableHeadCell padding="checkbox" />}
              {showCheckbox && (
                <StyledTableHeadCell padding="checkbox">
                  <Tooltip
                    title={
                      selectableItems.length === 0
                        ? 'No hay elementos seleccionables en esta página'
                        : isAllSelected
                          ? 'Deseleccionar todos los elementos de esta página'
                          : 'Seleccionar todos los elementos seleccionables de esta página'
                    }
                    placement="top"
                    arrow
                  >
                    <Box>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected && selectableItems.length > 0}
                        onChange={handleSelectAll}
                        size="small"
                        disabled={selectableItems.length === 0}
                        sx={getCheckboxStyles(selectableItems.length > 0)}
                      />
                    </Box>
                  </Tooltip>
                </StyledTableHeadCell>
              )}
              {columns.map((column) => (
                <StyledTableHeadCell
                  key={column.id}
                  align={column.align ?? 'left'}
                  style={column.headerStyle}
                >
                  {isColumnSortable(column) ? (
                    <TableSortLabel
                      active={effectiveSortBy === (column.sortField ?? column.id)}
                      direction={
                        effectiveSortBy === (column.sortField ?? column.id)
                          ? effectiveSortOrder
                          : 'asc'
                      }
                      onClick={() => handleHeaderSort(column.sortField ?? column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </StyledTableHeadCell>
              ))}
              {showActions && <StyledTableHeadCell align="right">Acción</StyledTableHeadCell>}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {displayedItems.length > 0 ? (
              displayedItems.map((item) => {
                const itemId = getItemId(item);
                const isItemSelectable = isSelectable ? isSelectable(item) : true;
                const isExpanded = expandedItemIds?.includes(itemId) ?? false;

                const canExpandRow = hasExpandToggle && isRowExpandable(item);

                return (
                  <React.Fragment key={itemId}>
                    <StyledTableRow
                      hover={!isExpanded}
                      sx={
                        isExpanded
                          ? (theme) => {
                              const openBg = getTableRowHoverOverlay(theme);
                              return {
                                backgroundColor: `${openBg} !important`,
                                '& > .MuiTableCell-root': { fontWeight: 700 },
                              };
                            }
                          : undefined
                      }
                    >
                      {hasExpandToggle && (
                        <StyledTableCell padding="checkbox">
                          {canExpandRow && (
                            <Tooltip title={isExpanded ? 'Ocultar detalle' : 'Ver detalle'}>
                              <IconButton
                                size="small"
                                onClick={() => onToggleExpand?.(itemId)}
                                aria-label={isExpanded ? 'Contraer fila' : 'Expandir fila'}
                                sx={(theme) => ({
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 1,
                                  p: 0.25,
                                  color: isExpanded ? 'primary.main' : 'text.secondary',
                                })}
                              >
                                {isExpanded ? (
                                  <ExpandRemoveIcon fontSize="small" />
                                ) : (
                                  <ExpandAddIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </StyledTableCell>
                      )}
                      {showCheckbox && (
                        <StyledTableCell padding="checkbox">
                          <Tooltip
                            title={
                              !isItemSelectable
                                ? `No se puede seleccionar: ${item.status ?? 'Estado no disponible'}`
                                : 'Haz clic para seleccionar este elemento'
                            }
                            placement="top"
                            arrow
                          >
                            <Box sx={{ position: 'relative' }}>
                              {isItemSelectable ? (
                                <Checkbox
                                  checked={selectedItems.includes(itemId)}
                                  onChange={() => handleSelectItem(itemId)}
                                  size="small"
                                  sx={getCheckboxStyles(true)}
                                />
                              ) : (
                                <DisabledCheckbox checked={false} disabled size="small" />
                              )}
                            </Box>
                          </Tooltip>
                        </StyledTableCell>
                      )}
                      {columns.map((column) => (
                        <StyledTableCell
                          key={column.id}
                          align={column.align ?? 'left'}
                          tone={cellTone}
                          style={column.cellStyle}
                        >
                          {column.render ? column.render(item) : (item as any)[column.id]}
                        </StyledTableCell>
                      ))}
                      {showActions && (
                        <StyledTableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            {renderActions ? (
                              renderActions(item)
                            ) : onViewDetails ? (
                              <Tooltip title="Ver detalles" placement="top" arrow>
                                <Box
                                  component="button"
                                  onClick={() => onViewDetails(itemId)}
                                  sx={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    padding: 0,
                                    color: 'primary.main',
                                    '&:hover': {
                                      opacity: 0.8,
                                    },
                                  }}
                                >
                                  👁️
                                </Box>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </StyledTableCell>
                      )}
                    </StyledTableRow>
                    {renderExpandedContent && (
                      <TableRow
                        sx={{
                          '&:hover': { backgroundColor: 'transparent' },
                          backgroundColor: 'background.paper',
                        }}
                      >
                        <TableCell
                          colSpan={totalColumns}
                          sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            {renderExpandedContent(item)}
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={totalColumns} align="center">
                  <EmptyStateMessage message={emptyStateTitle} minHeight={200} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </TableContainerStyled>
  );
}

export default BaseTable;
export type { BaseTableProps, TableColumn };
export type { TableItem } from './Table.hooks';
